import { useInView } from '@/hooks/useInView';

const stats = [
  { value: '100%', label: 'Care delivered in the client\'s own home' },
  { value: 'PVG', label: 'Checked staff, every single one' },
  { value: '1:1', label: 'Dedicated carer per client' },
  { value: 'CIS', label: 'Registered with Care Inspectorate Scotland' },
];

const commitments = [
  { title: 'Stay in your own home', desc: 'We believe people thrive best in familiar surroundings. Our care comes to you, not the other way around.' },
  { title: 'Safe and regulated', desc: 'Registered with the Care Inspectorate Scotland. Every carer is PVG checked and trained before their first visit.' },
  { title: 'Independence first', desc: 'Our goal is never to take over. We support you to do as much as you can, for as long as you can.' },
  { title: 'Flexible scheduling', desc: 'From a few hours a week to multiple visits a day, care is arranged around your routine, not ours.' },
];

export default function Mission() {
  const { ref: topRef, inView: topInView } = useInView<HTMLDivElement>({ threshold: 0.1 });
  const { ref: imgRef, inView: imgInView } = useInView<HTMLDivElement>({ threshold: 0.08 });
  const { ref: statsRef, inView: statsInView } = useInView<HTMLDivElement>({ threshold: 0.1 });
  const { ref: commitRef, inView: commitInView } = useInView<HTMLDivElement>({ threshold: 0.08 });

  return (
    <section className="overflow-hidden">

      {/* Mission text — light bg, editorial split */}
      <div className="bg-[#FAFAF8] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div
            ref={topRef}
            className="flex flex-col lg:flex-row lg:gap-20 lg:items-start transition-all duration-700 ease-out"
            style={{ opacity: topInView ? 1 : 0, transform: topInView ? 'none' : 'translateY(20px)' }}
          >
            <div className="lg:w-5/12 mb-10 lg:mb-0 lg:sticky lg:top-28">
              <p className="text-[#C0706A] text-[10px] tracking-[0.3em] uppercase font-semibold mb-6">
                Our Mission
              </p>
              <h2
                className="text-[#2C3E50] font-bold leading-[1.1]"
                style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}
              >
                Keeping people at home,{' '}
                <span className="text-[#C0706A] italic">where they belong.</span>
              </h2>
            </div>

            <div className="lg:w-7/12 space-y-5 text-[#2C3E50]/55 text-[15px] leading-relaxed">
              <p>
                At Aligned Care, we started with a simple belief: that most people, given the choice, would rather stay in their own home. Their own chair. Their own garden. Their own routine. And we think that choice deserves to be supported properly.
              </p>
              <p>
                In-home care is not a compromise. Done well, it is often the best possible option: preserving independence, maintaining dignity, and keeping people connected to the life they have built.
              </p>
              <p>
                We work across Scotland, supporting individuals and families with everything from a few hours of help each week to full daily care. Whatever the level of need, the approach is always the same: listen first, then build care around the person.
              </p>
              <div className="pt-2">
                <button
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className="inline-flex items-center gap-2 text-sm font-bold text-[#2C3E50] border-b border-[#2C3E50]/30 pb-0.5 hover:text-[#C0706A] hover:border-[#C0706A] transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  Talk to our team
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image break — full bleed split, text left / photo right */}
      <div
        ref={imgRef}
        className="flex flex-col lg:flex-row min-h-[420px] lg:min-h-[520px] transition-all duration-1000 ease-out"
        style={{ opacity: imgInView ? 1 : 0 }}
      >
        {/* Left — dark text panel */}
        <div className="w-full lg:w-2/5 bg-[#1C2B3A] flex flex-col justify-center px-8 md:px-14 lg:px-16 py-14 lg:py-0">
          <p className="text-[#D4A574] text-[10px] tracking-[0.3em] uppercase font-semibold mb-5">
            Our approach
          </p>
          <blockquote
            className="text-white font-bold leading-[1.2] mb-6"
            style={{ fontSize: 'clamp(1.4rem, 2.5vw, 2rem)' }}
          >
            &ldquo;We listen first. Then we build care around the person, not the other way around.&rdquo;
          </blockquote>
          <p className="text-white/45 text-sm leading-relaxed max-w-xs">
            Every client gets a dedicated carer who knows them, their preferences, and their routine. That consistency matters more than anything.
          </p>
        </div>

        {/* Right — photo */}
        <div className="w-full lg:w-3/5 h-[55vw] lg:h-auto relative overflow-hidden">
          <img
            src="https://images.pexels.com/photos/7551617/pexels-photo-7551617.jpeg?auto=compress&cs=tinysrgb&w=1600"
            alt="Aligned Home Care carer with client at home"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1C2B3A]/30 via-transparent to-transparent" />
        </div>
      </div>

      {/* Stats band — dark, full bleed */}
      <div className="bg-[#1C2B3A]">
        <div
          ref={statsRef}
          className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10 border-y border-white/10"
        >
          {stats.map((s, i) => (
            <div
              key={s.value}
              className="px-6 py-10 lg:py-12 transition-all ease-out"
              style={{
                opacity: statsInView ? 1 : 0,
                transform: statsInView ? 'none' : 'translateY(14px)',
                transitionDelay: `${i * 70}ms`,
                transitionDuration: '600ms',
              }}
            >
              <p
                className="text-[#C0706A] font-bold mb-2"
                style={{ fontSize: 'clamp(2rem, 4vw, 2.8rem)' }}
              >
                {s.value}
              </p>
              <p className="text-white/35 text-xs leading-snug">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Commitments — text only, no icons */}
      <div className="bg-[#FAFAF8] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div
            ref={commitRef}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-[#2C3E50]/10"
          >
            {commitments.map((c, i) => (
              <div
                key={c.title}
                className={`pt-8 pb-6 pr-8 transition-all ease-out ${i < 3 ? 'lg:border-r border-[#2C3E50]/10' : ''}`}
                style={{
                  opacity: commitInView ? 1 : 0,
                  transform: commitInView ? 'none' : 'translateY(14px)',
                  transitionDelay: `${i * 80}ms`,
                  transitionDuration: '600ms',
                }}
              >
                <span className="block text-[#C0706A]/30 text-[10px] font-mono tracking-widest mb-4">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="text-[#2C3E50] font-bold text-base mb-2">{c.title}</h3>
                <p className="text-[#2C3E50]/40 text-sm leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

    </section>
  );
}
